import { createParser } from 'eventsource-parser';
import type {
  AssistantRunRequest,
  AssistantStreamEvent,
} from '@xtreed/academy-shared';
import { API_BASE_URL, authHeaders, refreshAccessToken } from '@/services/api';

export class AssistantStreamError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

interface StreamRunOptions {
  conversationId: string;
  body: AssistantRunRequest;
  signal: AbortSignal;
  onEvent: (event: AssistantStreamEvent) => void;
}

async function open(
  conversationId: string,
  body: AssistantRunRequest,
  signal: AbortSignal,
): Promise<Response> {
  return fetch(
    `${API_BASE_URL}/assistant/conversations/${conversationId}/runs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authHeaders(),
      },
      body: JSON.stringify(body),
      signal,
    },
  );
}

async function errorCode(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { code?: string };
    return payload.code ?? `HTTP_${response.status}`;
  } catch {
    return `HTTP_${response.status}`;
  }
}

export async function streamAssistantRun({
  conversationId,
  body,
  signal,
  onEvent,
}: StreamRunOptions): Promise<void> {
  let response = await open(conversationId, body, signal);

  if (response.status === 401 && (await refreshAccessToken())) {
    response = await open(conversationId, body, signal);
  }

  if (!response.ok || !response.body) {
    throw new AssistantStreamError(await errorCode(response), response.status);
  }

  const parser = createParser({
    onEvent: (message) => {
      if (!message.data) return;
      try {
        onEvent(JSON.parse(message.data) as AssistantStreamEvent);
      } catch {
        return;
      }
    },
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.feed(decoder.decode(value, { stream: true }));
  }
  parser.feed(decoder.decode());
}
